import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ExplainModel } from './explainCell'
import { ExplainPanelContent } from './ExplainPanelContent'

function clampPosition(
  anchor: DOMRect | { x: number; y: number },
  width: number,
  height: number,
): { x: number; y: number } {
  const padding = 8
  const x = 'x' in anchor && !('top' in anchor) ? anchor.x : (anchor as DOMRect).left
  const y = 'x' in anchor && !('top' in anchor) ? anchor.y : (anchor as DOMRect).top
  const maxX = Math.max(padding, window.innerWidth - width - padding)
  const maxY = Math.max(padding, window.innerHeight - height - padding)
  return {
    x: Math.min(Math.max(x, padding), maxX),
    y: Math.min(Math.max(y, padding), maxY),
  }
}

export type ExplainPopoverProps = {
  model: ExplainModel
  anchor: DOMRect | { x: number; y: number }
  onClose: () => void
  canEdit?: boolean
  onSetOverrideReason?: (reason: string | null) => void
  onClearOverride?: () => void
}

export default function ExplainPopover({
  model,
  anchor,
  onClose,
  canEdit = false,
  onSetOverrideReason,
  onClearOverride,
}: ExplainPopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [reasonDraft, setReasonDraft] = useState(model.recipe?.override?.reason ?? '')
  const [position, setPosition] = useState(() => clampPosition(anchor, 320, 200))

  useEffect(() => {
    setReasonDraft(model.recipe?.override?.reason ?? '')
  }, [model.recipe?.override?.reason])

  useEffect(() => {
    const panel = panelRef.current
    if (!panel) {
      return
    }
    const rect = panel.getBoundingClientRect()
    setPosition(clampPosition(anchor, rect.width, rect.height))
  }, [anchor, model.header])

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      const target = event.target
      if (target instanceof Node && panelRef.current?.contains(target)) {
        return
      }
      onClose()
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }

    window.addEventListener('mousedown', handlePointerDown)
    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('mousedown', handlePointerDown)
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [onClose])

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-50 w-80 rounded-md border border-gray-200 bg-white p-3 shadow-lg"
      style={{ left: position.x, top: position.y }}
      onClick={(e) => e.stopPropagation()}
    >
      <p className="mb-3 text-sm font-medium text-gray-900">{model.header}</p>

      <ExplainPanelContent
        model={model}
        canEdit={canEdit}
        reasonDraft={reasonDraft}
        onReasonDraftChange={setReasonDraft}
        onReasonDraftBlur={() => onSetOverrideReason?.(reasonDraft.trim() || null)}
        onClearOverride={onClearOverride}
      />
    </div>,
    document.body,
  )
}
